import * as Sentry from '@sentry/react';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import type { BenchmarkScoring, BenchmarkScoringProgress, ListBenchmarkScoringsQueryVariables } from '@vizit/api';
import { getAPIErrorCode, useGetBenchmarkScoringQuery, useListBenchmarkScoringsQuery } from '@vizit/api';
import { useRequestBenchmarkScoringMutation, useSelectBenchmarkScoringMutation } from '@vizit/api';
import { SENTRY_FEATURE_TAGS, SENTRY_STAGE_TAGS } from '@vizit/constants';
import type { PickerProductCategory } from '@vizit/organisms';
import * as React from 'react';
import { refreshBenchmark } from './refresh-benchmark';
import { benchmarkScoringStatusRefetchInterval } from './use-benchmark-scorings.polling';
import { benchmarkScoringsRefetchInterval } from './use-benchmark-scorings.polling';

/**
 * A scoring as the category picker lists it. The listing names the category; a scoring just requested does not, so
 * the organization's category (from `useFindProductCategory`: a list the add dialog showed, else the organization's
 * lookup) fills the name and gives the color, the parents and the retailer that tell same-name categories apart.
 */
function toPickerCategory(
  scoring: Pick<BenchmarkScoring, 'productCategoryId' | 'productCategoryName'>,
  category?: PickerProductCategory | null,
): PickerProductCategory {
  return {
    id: scoring.productCategoryId,
    name: scoring.productCategoryName ?? category?.name ?? 'Product category',
    color: category?.color,
    parents: category?.parents,
    retailer: category?.retailer,
  };
}

/** What requesting scorings came to: the ones the API accepted and the categories it refused. */
interface RequestScoringsResult<TScoring = BenchmarkScoring> {
  requested: TScoring[];
  failedCategoryIds: string[];
  /** The API's `error_code` for each refused category, when it sent one */
  failureCodes?: Record<string, string | undefined>;
}

interface UseBenchmarkScoringsProps {
  workspaceId?: string;
  benchmarkId: string;
  /**
   * When the record of the latest upload to reach storage was created, from `useBenchmarkImageUpload`. The API then
   * scores the new image under every category the benchmark is scored under by requesting those scorings again; the
   * list is re-read until that shows, so their status polls start.
   */
  uploadLandedAt?: string | null;
}

/**
 * The product categories a benchmark is scored under, split by state, plus the two writes: requesting scorings for
 * new categories and recording which one the user picked. Each pending scoring is polled on its status route (see
 * the polling module); when one lands, ready or failed, the benchmark is refreshed: this list, its image lists and
 * the benchmark itself. Failed scorings are kept apart so the picker can show them; their categories can be
 * requested again.
 */
function useBenchmarkScorings({ workspaceId, benchmarkId, uploadLandedAt = null }: UseBenchmarkScoringsProps) {
  const queryClient = useQueryClient();
  const variables: ListBenchmarkScoringsQueryVariables = { workspace_id: workspaceId ?? '', benchmark_id: benchmarkId };
  const { data, isLoading, isError, isSuccess } = useListBenchmarkScoringsQuery(variables, {
    enabled: !!workspaceId && !!benchmarkId,
    // Only ever polls right after an upload lands; see the polling module.
    refetchInterval: benchmarkScoringsRefetchInterval(variables, uploadLandedAt),
  });
  const { mutateAsync: requestScoring } = useRequestBenchmarkScoringMutation();
  const { mutateAsync: selectScoring } = useSelectBenchmarkScoringMutation();

  const scorings = React.useMemo(() => data ?? [], [data]);
  const scored = React.useMemo(() => scorings.filter((scoring) => scoring.status === 'ready'), [scorings]);
  const processing = React.useMemo(() => scorings.filter((scoring) => scoring.status === 'pending'), [scorings]);
  const failed = React.useMemo(() => scorings.filter((scoring) => scoring.status === 'failed'), [scorings]);

  // The listing comes most recently selected first; requesting a scoring stamps it too, so a category just added
  // reopens as the selection while it scores.
  const lastSelected = scorings.find((scoring) => scoring.lastSelectedAt) ?? null;

  const refresh = React.useCallback(
    () =>
      queryClient.invalidateQueries({
        queryKey: useListBenchmarkScoringsQuery.getKey({ workspace_id: workspaceId ?? '', benchmark_id: benchmarkId }),
      }),
    [queryClient, workspaceId, benchmarkId],
  );

  // One status poll per pending scoring; each stops on its own once the scoring lands.
  const statusQueries = useQueries({
    queries: processing.map((scoring) => {
      const statusVariables = { workspace_id: workspaceId ?? '', benchmark_id: benchmarkId, scoring_id: scoring.id };
      return {
        // A scoring requested again keeps its id, and its earlier run's status, cached as landed and fresh for the
        // app's stale time, would answer for it. Keyed on the request time too, each run reads its status fresh.
        queryKey: [...useGetBenchmarkScoringQuery.getKey(statusVariables), scoring.requestedAt],
        queryFn: useGetBenchmarkScoringQuery.fetcher(statusVariables),
        enabled: !!workspaceId,
        refetchInterval: benchmarkScoringStatusRefetchInterval(statusVariables),
      };
    }),
  });
  // Cheap enough to rebuild each render; `useQueries` hands back a new array every time anyway.
  const progress = new Map<string, BenchmarkScoringProgress>(
    statusQueries.flatMap((query) => (query.data ? [[query.data.productCategoryId, query.data]] : [])),
  );

  // A scoring that landed makes the list stale and gives the benchmark's images their scores.
  const landedIds = statusQueries
    .filter((query) => query.data && query.data.status !== 'pending')
    .map((query) => query.data?.id)
    .join(',');
  React.useEffect(() => {
    if (!landedIds || !workspaceId) {
      return;
    }
    refreshBenchmark(queryClient, workspaceId, benchmarkId);
  }, [landedIds, queryClient, workspaceId, benchmarkId]);

  /**
   * Scores the benchmark under each category, all at once; resolves with the scorings the API accepted and the
   * categories it refused, so the caller can retry only those.
   */
  const requestScorings = React.useCallback(
    async (categoryIds: string[]): Promise<RequestScoringsResult> => {
      if (!workspaceId) {
        return { requested: [], failedCategoryIds: categoryIds };
      }
      const settled = await Promise.allSettled(
        categoryIds.map((categoryId) =>
          requestScoring({ workspace_id: workspaceId, benchmark_id: benchmarkId, product_category_id: categoryId }),
        ),
      );
      const result: RequestScoringsResult = { requested: [], failedCategoryIds: [] };
      settled.forEach((outcome, index) => {
        const categoryId = categoryIds[index] as string;
        if (outcome.status === 'fulfilled') {
          result.requested.push(outcome.value);
          return;
        }
        result.failedCategoryIds.push(categoryId);
        Sentry.captureException(outcome.reason, {
          tags: {
            feature: SENTRY_FEATURE_TAGS.Benchmark,
            stage: SENTRY_STAGE_TAGS[SENTRY_FEATURE_TAGS.Benchmark].benchmarkScoringRequest,
          },
          extra: { workspace_id: workspaceId, benchmark_id: benchmarkId, product_category_id: categoryId },
        });
      });
      // Whatever got through is now listed, pending.
      await refresh();
      return result;
    },
    [workspaceId, benchmarkId, requestScoring, refresh],
  );

  /**
   * Records that the user switched to a category. Best effort: the page already shows it. A scoring just requested
   * is not in the list yet, so a caller holding it passes it along.
   */
  const recordSelection = React.useCallback(
    (categoryId: string, known?: BenchmarkScoring) => {
      const scoring = known ?? scorings.find((candidate) => candidate.productCategoryId === categoryId);
      if (!workspaceId || !scoring) {
        return;
      }
      selectScoring({ workspace_id: workspaceId, benchmark_id: benchmarkId, scoring_id: scoring.id })
        // The cached list keeps its `lastSelectedAt` fresh, so a remount reopens on this category.
        .then(() => refresh())
        .catch((error) => {
          // A benchmark under its minimum is refused for good; its page already says why, so it is not an error here.
          if (getAPIErrorCode(error) === 'BENCHMARK_TOO_SMALL') {
            return;
          }
          Sentry.captureException(error, {
            level: 'warning',
            tags: {
              feature: SENTRY_FEATURE_TAGS.Benchmark,
              stage: SENTRY_STAGE_TAGS[SENTRY_FEATURE_TAGS.Benchmark].benchmarkScoringSelect,
            },
            extra: { workspace_id: workspaceId, benchmark_id: benchmarkId, scoring_id: scoring.id },
          });
        });
    },
    [workspaceId, benchmarkId, scorings, selectScoring, refresh],
  );

  return {
    scorings,
    scored,
    processing,
    failed,
    /** Live progress of each pending scoring, by product category ID */
    progress,
    lastSelected,
    // A disabled query reports isLoading false, so a workspace that has not resolved yet counts as loading.
    isLoading: isLoading || !workspaceId,
    /** The list has been read at least once; image requests that depend on a category wait for this */
    isResolved: isSuccess || isError,
    isError,
    requestScorings,
    recordSelection,
  };
}

export { toPickerCategory, useBenchmarkScorings };
export type { RequestScoringsResult };
